import React, { useState, useMemo } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import DashboardMain from "./Pages/dashboard/index";
import Subdealers from "./Pages/users";
import SubdealerDetail from "./Pages/subdealers/SubdealerDetail";
import Branches from "./Pages/branches";
import BranchDetail from "./Pages/branches/BranchDetail";
import Managers from "./Pages/managers";
import ManagerDetail from "./Pages/managers/ManagerDetail";
import Dealers from "./Pages/dealers";
import DealerDetail from "./Pages/dealers/DealerDetail";
import WinCars from "./Pages/winCars";
import Accounts from "./Pages/accounts";
import Tools from "./Pages/tools";
import CalculatorEmbedPage from "./Pages/tools/CalculatorEmbedPage";
import ModernLayout from "./components/ModernLayout";
import Statistics from "./Pages/statistics/LotEvents";
import Login from "./Pages/auth/Login";
import ForgotPassword from "./Pages/auth/ForgotPassword";
import Settings from "./Pages/settings";
import AuctionFees from "./Pages/auctionFees";
import DeliveryCost from "./Pages/deliveryCost";
import WatchlistPage from "./Pages/watchlist";
import CalculatorDestinationsPage from "./Pages/calculatorDestinations/CalculatorDestinationsPage";
import CalculatorVehicleTypesPage from "./Pages/calculatorVehicleTypes/CalculatorVehicleTypesPage";
import CalculatorEngineTypesPage from "./Pages/calculatorEngineTypes/CalculatorEngineTypesPage";
import {
  CALCULATOR_SETTINGS_BASE,
  CALCULATOR_SETTINGS_DESTINATIONS_PATH,
  CALCULATOR_SETTINGS_ENGINE_TYPES_PATH,
  CALCULATOR_SETTINGS_VEHICLE_TYPES_PATH,
  LEGACY_GEORGIAN_CALCULATOR_SETTINGS_BASE,
} from './config/calculatorSettings';
import { getAntdThemeConfig } from './themeConfig';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { TranslationProvider } from './i18n/TranslationContext';
import { LocalizedAntdConfig } from './i18n/LocalizedAntdConfig';
import { DemoProvider } from './context/DemoContext';

/** Public /tools/calculator must wrap the tree that uses `useTranslation` (VehicleImportCalculator). */
function CalculatorEmbedRoute({ theme }) {
  return (
    <TranslationProvider>
      <LocalizedAntdConfig theme={theme}>
        <CalculatorEmbedPage />
      </LocalizedAntdConfig>
    </TranslationProvider>
  );
}

/** Old flat path from pre–calculator-settings URL scheme */
function LegacyCalculatorDestinationsRedirect() {
  return <Navigate to={CALCULATOR_SETTINGS_DESTINATIONS_PATH} replace />;
}

function App() {
  const [mode, setMode] = useState('dark');

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const themeConfig = useMemo(() => getAntdThemeConfig(mode), [mode]);

  const loginTheme = useMemo(() => getAntdThemeConfig('light'), []);

  return (
    <Router>
      <AuthProvider>
        <DemoProvider>
          <Routes>
            <Route
              path="/login"
              element={
                <TranslationProvider>
                  <LocalizedAntdConfig theme={loginTheme}>
                    <Login />
                  </LocalizedAntdConfig>
                </TranslationProvider>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <TranslationProvider>
                  <LocalizedAntdConfig theme={loginTheme}>
                    <ForgotPassword />
                  </LocalizedAntdConfig>
                </TranslationProvider>
              }
            />
              {/*
                Dev hosts may 301 /tools/calculator → /tools/calculator/ — register both, plus splat.
                Autobuy-dashboard mirrors the same set.
              */}
              <Route path="/tools/calculator" element={<CalculatorEmbedRoute theme={themeConfig} />} />
              <Route path="/tools/calculator/" element={<CalculatorEmbedRoute theme={themeConfig} />} />
              <Route path="/tools/calculator/*" element={<CalculatorEmbedRoute theme={themeConfig} />} />
              <Route path="/:lang/tools/calculator" element={<CalculatorEmbedRoute theme={themeConfig} />} />
              <Route path="/:lang/tools/calculator/" element={<CalculatorEmbedRoute theme={themeConfig} />} />
              <Route path="/:lang/tools/calculator/*" element={<CalculatorEmbedRoute theme={themeConfig} />} />
              <Route
                path=":lang"
                element={
                  <TranslationProvider>
                    <LocalizedAntdConfig theme={themeConfig}>
                      <ProtectedRoute>
                        <ModernLayout toggleColorMode={toggleColorMode} mode={mode} />
                      </ProtectedRoute>
                    </LocalizedAntdConfig>
                  </TranslationProvider>
                }
              >
                <Route path="dashboard" element={<DashboardMain />} />
                <Route path="branches" element={<Branches />} />
                <Route path="branches/:branchId" element={<BranchDetail />} />
                <Route path="managers" element={<Managers />} />
                <Route path="managers/:managerId" element={<ManagerDetail />} />
                <Route path="dealers" element={<Dealers />} />
                <Route path="dealers/:dealerId" element={<DealerDetail />} />
                <Route path="subdealers" element={<Subdealers />} />
                <Route path="subdealers/:subdealerId" element={<SubdealerDetail />} />
                <Route path="win-cars" element={<WinCars />} />
                <Route path="accounts" element={<Accounts />} />
                <Route path="auction-fees" element={<AuctionFees />} />
                <Route path="delivery-cost" element={<DeliveryCost />} />
                <Route path="statistics" element={<Statistics />} />
                <Route path="watchlist" element={<WatchlistPage />} />
                <Route path="tools" element={<Tools />} />
                <Route
                  path={CALCULATOR_SETTINGS_BASE}
                  element={<Navigate to={CALCULATOR_SETTINGS_DESTINATIONS_PATH} replace />}
                />
                <Route path={CALCULATOR_SETTINGS_DESTINATIONS_PATH} element={<CalculatorDestinationsPage />} />
                <Route path={CALCULATOR_SETTINGS_VEHICLE_TYPES_PATH} element={<CalculatorVehicleTypesPage />} />
                <Route path={CALCULATOR_SETTINGS_ENGINE_TYPES_PATH} element={<CalculatorEngineTypesPage />} />
                <Route
                  path={LEGACY_GEORGIAN_CALCULATOR_SETTINGS_BASE}
                  element={<Navigate to={CALCULATOR_SETTINGS_DESTINATIONS_PATH} replace />}
                />
                <Route
                  path={`${LEGACY_GEORGIAN_CALCULATOR_SETTINGS_BASE}/destinations`}
                  element={<Navigate to={`../${CALCULATOR_SETTINGS_DESTINATIONS_PATH}`} replace />}
                />
                <Route
                  path={`${LEGACY_GEORGIAN_CALCULATOR_SETTINGS_BASE}/vehicle-types`}
                  element={<Navigate to={`../${CALCULATOR_SETTINGS_VEHICLE_TYPES_PATH}`} replace />}
                />
                <Route
                  path={`${LEGACY_GEORGIAN_CALCULATOR_SETTINGS_BASE}/engine-types`}
                  element={<Navigate to={`../${CALCULATOR_SETTINGS_ENGINE_TYPES_PATH}`} replace />}
                />
                <Route
                  path="calculator-destinations"
                  element={<LegacyCalculatorDestinationsRedirect />}
                />
                <Route path="settings" element={<Settings />} />
              </Route>
              <Route
                path="/"
                element={<Navigate to="/login" replace />}
              />
              <Route
                path="*"
                element={<Navigate to="/login" replace />}
              />
          </Routes>
        </DemoProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
